/**
 * HeaderMobileMenu.tsx
 *
 * File-level: Mobile menu panel component used by Header.
 * This V145-style mobile menu is a full-screen overlay with a sliding panel,
 * large navigation items, a prominent logo area, and CTA at the bottom.
 *
 * Behaviour:
 * - Opens with smooth slide + fade animation
 * - Closes on backdrop click, link click, Escape key, or Close button
 * - Locks body scroll while open and restores focus to provided ref on close
 */

import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router'
import { X } from 'lucide-react'
import { Button } from './ui/button'

/**
 * Props for MobileMenu component
 *
 * @interface MobileMenuProps
 */
interface MobileMenuProps {
  /** Whether the mobile menu is open */
  isOpen: boolean
  /** Callback to close the mobile menu */
  onClose: () => void
  /** Optional ref to the toggle button so focus can be restored after closing */
  returnFocusRef?: React.RefObject<HTMLButtonElement>
}

/**
 * MobileMenu component
 *
 * Component-level: Displays a V145-styled full-screen mobile navigation panel when isOpen
 * is true. Handles Escape key, backdrop click, body-scroll lock, and focus restoration.
 *
 * @param {MobileMenuProps} props
 * @returns {JSX.Element | null}
 */
export default function MobileMenu({
  isOpen,
  onClose,
  returnFocusRef
}: MobileMenuProps): JSX.Element | null {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen) return

    /**
     * Handle Escape key to close the menu.
     *
     * @param {KeyboardEvent} e
     */
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    // Lock body scroll while menu is open
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    window.addEventListener('keydown', handleKey)

    // Focus the close button for keyboard users
    setTimeout(() => {
      closeBtnRef.current?.focus()
    }, 50)

    return () => {
      window.removeEventListener('keydown', handleKey)
      document.body.style.overflow = previousOverflow
      // restore focus to the toggle button if supplied
      if (returnFocusRef && returnFocusRef.current) {
        returnFocusRef.current.focus()
      }
    }
  }, [isOpen, onClose, returnFocusRef])

  if (!isOpen) return null

  /**
   * Handle backdrop clicks by closing when clicking outside the panel.
   *
   * @param {React.MouseEvent} e
   */
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      onClose()
    }
  }

  // Nav items — keep in sync with the desktop header links
  const navItems = [
    { label: 'Home', to: '/' },
    { label: 'About Us', to: '/about' },
    { label: 'Services', to: '/services' },
    { label: 'Portfolio', to: '/portfolio' },
    { label: 'Team', to: '/team' },
    { label: 'Contact', to: '/contact' }
  ]

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Mobile navigation"
      className="fixed inset-0 z-50 flex"
      onMouseDown={handleBackdropClick}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-sm transition-opacity"
        aria-hidden="true"
      />

      {/* Sliding panel */}
      <div
        ref={panelRef}
        className="relative ml-auto w-full max-w-[420px] bg-white shadow-xl h-full transform transition duration-300 ease-in-out translate-x-0"
        style={{ willChange: 'transform, opacity' }}
      >
        {/* Header: logo + close */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <Link to="/" onClick={onClose} className="inline-flex items-center gap-3">
            <img
              src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/f2dcbeba-4716-4434-8b8b-01acce34f085.png"
              alt="Edicoo logo"
              className="h-10 w-auto object-contain"
              onError={(e) => {
                ;(e.currentTarget as HTMLImageElement).src =
                  'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/266987bf-c3f8-4c4b-98fa-02f33d6e57a7.png'
              }}
            />
          </Link>

          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close navigation"
            className="rounded-lg p-2 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
          >
            <X className="h-5 w-5 text-gray-700" />
          </button>
        </div>

        {/* Nav list */}
        <nav className="px-6 py-8">
          <ul className="flex flex-col gap-6">
            {navItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={onClose}
                  className="block text-2xl font-semibold text-gray-800 hover:text-orange-500 transition-colors"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-auto px-6 pb-8">
          <div className="flex flex-col gap-3">
            {/* CTA button - use outline variant with bg-transparent per OutlineButtonFix */}
            <Button asChild variant="outline" className="bg-transparent w-full">
              <Link to="/contact" onClick={onClose} className="w-full text-center">
                Contact Us
              </Link>
            </Button>

            {/* Small utility links */}
            <div className="flex items-center justify-between text-sm text-gray-600 mt-2">
              <Link to="/about" onClick={onClose} className="hover:underline">
                About
              </Link>
              <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="hover:underline">
                Facebook
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}